/* eslint-disable @typescript-eslint/no-unused-vars */

import {inject, Injectable} from '@angular/core';
import {SnackbarService} from '../../snackbar/snackbar.service';
import {AppSnackbarTypeEnum} from '../../../enums/modal/app-snackbar-type.enum';
import {ModalService} from '../../modal/modal.service';
import {LocalFileService} from '../local-file.service';
import {SharePlugin, ShareResult} from '@capacitor/share';
import {Directory, Filesystem, WriteFileResult} from '@capacitor/filesystem';
import {Vault} from '../../../api/models/vault';
import {registerPlugin} from '@capacitor/core';
import {FileOpener} from '@capacitor-community/file-opener';
import {checkFileSystemPermissions} from '../../../util/check-file-system-permissions.util';
import {arrayBufferToBase64} from '../../../util/arraybuffer-to-base64.util';
import {LoggingService} from '../../logging/logging.service';
import mime from 'mime';

@Injectable()
export class AndroidLocalFileService implements LocalFileService {
    private readonly snackbarService = inject(SnackbarService);
    private readonly modalService = inject(ModalService);
    private readonly loggingService = inject(LoggingService);
    private readonly appSharePlugin = registerPlugin<SharePlugin>('AppShare');

    public async saveFile(content: ArrayBuffer, filename: string, openFile: boolean = false): Promise<void> {
        await checkFileSystemPermissions();
        const snackbar = this.snackbarService.showPermanentSnackbar(AppSnackbarTypeEnum.Info, 'permanent-snackbar-FILE_DOWNLOAD_IS_DOWNLOADING', 'FILE_DOWNLOAD_IS_DOWNLOADING');
        let result: WriteFileResult | undefined;
        try {
            result = await Filesystem.writeFile({
                path: filename,
                data: arrayBufferToBase64(content),
                directory: Directory.Documents
            });
        } catch (error) {
            console.error('Error while saving file at path ' + this.loggingService.maskPath(Directory.Documents + '/' + filename) + ': ' + error);
        }

        if (result) {
            if (openFile) {
                FileOpener.open({
                        filePath: result.uri,
                        contentType: mime.getType(filename) ?? undefined,
                        openWithDefault: true,
                    })
                    .then(() => {
                        console.log(filename, 'File is opened');
                    }, (err: unknown) => {
                        console.error('Error opening file at path ' + this.loggingService.maskPath(result.uri) + ': ' + err);
                    });
            }
            this.modalService.closeModal(snackbar);
            this.snackbarService.showSuccess('FILE_DOWNLOAD_SUCCESS_SHORT_MSG');
        } else {
            this.modalService.closeModal(snackbar);
        }
    }

    public async shareFile(content: ArrayBuffer, filename: string): Promise<void> {
        await Filesystem.writeFile({
            path: filename,
            data: arrayBufferToBase64(content),
            directory: Directory.Cache
        });

        const uriResult = await Filesystem.getUri({
            path: filename,
            directory: Directory.Cache
        });

        try {
            await this.appSharePlugin.share({
                url: uriResult.uri
            });
        } catch (error) {
            console.error('Error while sharing: ', error);
            return;
        }
    }

    public async shareUrl(url: string): Promise<ShareResult> {
        const options = (url.includes('http')) ? { url } : { text: url };
        let result: ShareResult | undefined;
        let errorMessage = '';
        try {
            result = await this.appSharePlugin.share(options);
        } catch (error) {
            errorMessage = (error as Error).toString();
        }
        return new Promise<ShareResult>((resolve, reject) => {
            if (errorMessage.length > 0) {
                if (errorMessage.includes('cancel')) {
                    resolve({ activityType: 'canceled' });
                    return;
                } else {
                    reject(errorMessage);
                }
            } else {
                if (result) {
                    resolve(result);
                } else {
                    reject(new Error('unknown error'));
                }
            }
        });
    }

    public async createVaultDirectory(vault: Vault): Promise<void> {
    }
}
